const axios = require('axios');
const baseLanguageStrings = require('@temp/js/en');
const DateFormat = require('@/lib/Dates/DateFormat');
const LangString = require('@/lib/Lang/LangString');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const { extendDeep } = require('@/functions/functions');
const { getPropertyByDotNotation } = require('@/functions/objects');
const { getSubdirectory } = require('@/functions/location');
const { parseIni } = require('@/functions/ini');
const { ucfirst } = require('@/functions/strings');

/**
 * @constructor
 *
 * @param $interpolate
 */
const LangService = function (
    $interpolate
) {
    this.$interpolate = $interpolate;

    this.log = logFactory('LangService');

    this.baseLanguage = 'en';
    this.currentLanguage = 'en';
    this.strings = baseLanguageStrings;

    this.branchId = null;

    /**
     * @type {Object.<string, Promise>}
     */
    this.loadLanguageFilesPromises = {};
};

LangService.prototype = {
    /**
     * @param {Branch} branch
     */
    setLanguageFromBranch: function (branch) {
        if (branch && branch.language !== null) {
            this.setLanguage(
                branch.language,
                branch.languageOverrides,
                branch.ID
            );
        }
    },

    /**
     * Switch language.
     * The basis of all languages is the en.ini file stored in the /lang dir, and that is the default language.
     * Note that in our case the base 'en' is actually 'en-gb'.
     * 'en-us' would be an override on top of this.
     *
     * If a branch uses a different language, the strings in that language override the base language (en).
     * And if a branch has custom language strings, they further override that.
     *
     * @param {string} newLanguage Name of the new language file to load
     * @param {object} overrides   Any strings in this object will replace those in the loaded language file
     * @param {number} branchId
     */
    setLanguage: function (newLanguage, overrides, branchId) {
        // No language given? Nothing to do
        if (!newLanguage) {
            return;
        }

        // Default value for overrides
        if (!overrides) {
            overrides = {};
        }

        // Already the language in use?
        // The branchId check is so that we know the same overrides are being used as well
        // (easier to compare the branch ID than override objects)
        if (this.currentLanguage === newLanguage && this.branchId === branchId) {
            this.log.debug('Language ' + newLanguage + ' for branch ' + branchId + ' already loaded.');
            return;
        }

        // Default language is easy since it's baked in to the JS
        if (newLanguage === this.baseLanguage) {
            this.strings = extendDeep(extendDeep({}, baseLanguageStrings), overrides);
            this.currentLanguage = newLanguage;
            this.branchId = branchId;
            return;
        }

        // Non default language, need to load it...
        this.loadLanguageStringsFromFile(newLanguage)
            .then((newLanguageStrings) => {
                // If we can't load that language, fallback to default language
                if (!newLanguageStrings) {
                    this.log.error('Failed to load language file ' + newLanguage + '.ini');
                    this.strings = extendDeep(extendDeep({}, baseLanguageStrings), overrides);
                    this.currentLanguage = this.baseLanguage;
                    return;
                }

                this.strings = extendDeep(extendDeep(extendDeep({}, baseLanguageStrings), newLanguageStrings), overrides);
                this.currentLanguage = newLanguage;
                this.branchId = branchId;
            });
    },

    /**
     * Reads in a language ini file, parses it, and returns an object.
     *
     * @param  {string} lang Name of the language file to load from the /lang dir.
     *
     * @return {Promise<Object>}
     */
    loadLanguageStringsFromFile: function (lang) {
        if (!this.loadLanguageFilesPromises[lang]) {
            const path = `/${getSubdirectory()}/lang/${lang}.ini`;
            this.log.debug('Loading language file ' + path);

            this.loadLanguageFilesPromises[lang] = axios
                .get(path)
                .then((response) => {
                    if (response.data) {
                        return parseIni(response.data);
                    }
                    return null;
                });
        }

        return this.loadLanguageFilesPromises[lang];
    },

    /**
     * Returns a string from the language file.
     * This is what the 'lang' filter is calling and is is safe to directly call
     * LangService.getText() to achieve the same result rather than injecting angular's $filter.
     *
     * @param {string} key
     * @param {object} [parameters]
     *
     * @return {string}
     */
    getText: function (key, parameters) {
        // Accept LangString objects as well as plain strings.
        if (key instanceof LangString) {
            parameters = key.params;
            key = key.key;
        }

        // If we can't find what to display, show this
        let fallbackText = ucfirst(key.split('.').pop().toLowerCase());
        fallbackText = fallbackText.replace(/_/g, ' ');

        // Haven't loaded any strings
        if (this.strings.length < 1) {
            console.error('Unable to load string (no strings loaded)', key);
            return fallbackText;
        }

        let text = getPropertyByDotNotation(this.strings, key);
        if (!text) {
            // Try removing the category from the key and use the global key if possible
            let arr = key.split('.');

            // Looking for a global anyway, fail
            if (arr.length < 2) {
                console.error('Unable to load string', key);
                return fallbackText;
            }

            // Remove first item from the array of keys
            arr.shift();
            let strippedKey = arr.join('.');
            text = getPropertyByDotNotation(this.strings, strippedKey);

            // Still nothing, fail
            if (!text) {
                console.error('Unable to load string', key, strippedKey);
                return fallbackText;
            }
        }

        if (typeof text !== 'string') {
            console.error('Unable to load string (unexpected type)', key);
            return fallbackText;
        }

        // Got the text now

        if (!parameters || Object.keys(parameters).length < 1) {
            // No parameters so no need to interpolate
            return text;
        }

        // Add DateFormat to the parameters so we can use moment filters in date strings.
        parameters.DateFormat = DateFormat;

        // Parameters were given, so run it through the interpolator
        return this.$interpolate(text)(parameters);
    },

    /**
     * Check if a lang string exists.
     *
     * @param {string} key
     *
     * @return {boolean}
     */
    hasKey: function (key) {
        return typeof getPropertyByDotNotation(this.strings, key) === 'string';
    },

    /**
     * Returns a string from the language file but does not have any fallback. This function can be used as an
     * alternative to getText() for optional text that may not have a value set. See customTemplateTextDirective.
     *
     * @param {string} key
     * @param {object} [parameters]
     *
     * @return {string|null}
     */
    getTextWithoutFallback: function (key, parameters) {
        // Haven't loaded any strings
        if (this.strings.length < 1) {
            console.error('Unable to load string (no strings loaded)', key);
            return null;
        }

        let text = getPropertyByDotNotation(this.strings, key);

        if (typeof text !== 'string') {
            console.error('Unable to load string (unexpected type)', key);
            return null;
        }

        if (!parameters || Object.keys(parameters).length < 1) {
            // No parameters so no need to interpolate
            return text;
        }

        // Parameters were given, so run it through the interpolator
        return this.$interpolate(text)(parameters);
    },
};

module.exports = LangService;
